import { axisPositions, cellIndexAt } from '../grid/cells.js';
import { boardSquares, footprintSquares } from './fog.js';
import { areaSpans } from './areas.js';
import { squares3D, heightSquares } from '../grid/measure.js';
/** What a system with no area rule is told, in place of a list. */
export const CAUGHT_NO_RULE = "This table's game system names no rule for who an area catches, so no list is made.";
/** The squares, across or down, that meet at a coordinate: one, or the two either side of a line it lies on. */
function centreIndices(positions, at) {
    const last = positions.length - 2;
    for (let k = 1; k <= last; k += 1)
        if (Math.abs(positions[k] - at) < 0.5)
            return [k - 1, k];
    return [Math.max(0, Math.min(last, cellIndexAt(positions, at)))];
}
/**
 * Where a token's space lies across one axis, before the board clamps it: `in` wholly on the board, `out` wholly off
 * it, `part` across its edge. `c` is the token's centre and `n` its size in squares, measured in the edge square's
 * pitch (half a pixel of slack, as `footprintSquares` steps inside an edge).
 */
function onBoard(positions, c, n) {
    const last = positions.length - 1;
    if (last < 1)
        return 'in';
    const lo = positions[0];
    const hi = positions[last];
    const from = c - (n * (positions[1] - lo)) / 2;
    const to = c + (n * (hi - positions[last - 1])) / 2;
    if (to <= lo + 0.5 || from >= hi - 0.5)
        return 'out';
    return from >= lo - 0.5 && to <= hi + 0.5 ? 'in' : 'part';
}
/**
 * The tokens `area` catches, top row first then left to right; null when the area was laid with no rule for it
 * (`CAUGHT_NO_RULE`). Props are scenery, never caught.
 */
export function caughtBy(area, objects, grid, extent, height) {
    const rule = area.includes;
    if (!rule)
        return null;
    const { cols, rows } = boardSquares(grid, extent);
    const inArea = new Set();
    for (const s of areaSpans(area, cols, rows))
        for (let i = s.i0; i <= s.i1; i += 1)
            inArea.add(s.j * cols + i);
    const all = caughtIn((i, j) => inArea.has(j * cols + i), rule, objects, grid, extent);
    // A cone or a line starts from its maker (qa-runs 1443, F8): a creature whose space holds the origin corner inside
    // it, not on its edge, is the one breathing or casting it, and is never caught by it — a dragon's Fire Breath laid
    // from the middle of its own space. A burst's origin can sit inside a large target, so it is left alone.
    // The maker named on the area (qa-runs#1451) is left out however its space meets the origin: a one-square dragon's
    // breath starts on a corner of its own square, which the inside-the-space test above cannot tell from a neighbour's.
    const flat = area.shape !== 'cone' && area.shape !== 'line' ? all : all.filter((o) => {
        if (area.maker !== undefined && o.id === area.maker)
            return false;
        const box = footprintSquares(grid, extent, { x: o.x, y: o.y }, o.size);
        return !(area.origin.i > box.i0 && area.origin.i <= box.i1 && area.origin.j > box.j0 && area.origin.j <= box.j1);
    });
    if (!height)
        return flat;
    // Height (qa-runs#1160; design/32 §3): a token above or below the origin is caught only where a square of its space in
    // the area is within the area's size counting the height between, by the same rule (a 20-ft burst on the ground misses
    // a griffon 40 ft up). A rectangle reaches as high as its longer side.
    const reachOf = area.shape === 'rect' ? Math.max(area.size, area.size2) : area.size;
    return flat.filter((o) => {
        const dz = heightSquares(height(o) - (area.z ?? 0), area.diagonal);
        if (!dz)
            return true;
        const box = footprintSquares(grid, extent, { x: o.x, y: o.y }, o.size);
        for (let j = box.j0; j <= box.j1; j += 1)
            for (let i = box.i0; i <= box.i1; i += 1) {
                if (!inArea.has(j * cols + i))
                    continue;
                const dx = i >= area.origin.i ? i - area.origin.i + 1 : area.origin.i - i;
                const dy = j >= area.origin.j ? j - area.origin.j + 1 : area.origin.j - j;
                if (squares3D(area.shape === 'rect' ? 0 : dx, area.shape === 'rect' ? 0 : dy, dz, area.diagonal) <= reachOf)
                    return true;
            }
        return false;
    });
}
/**
 * The tokens a set of squares catches by `rule`, as `caughtBy` lists them: the one selection an area and an aura (issue
 * 1150, `auras.ts`) share. `inside(i, j)` says whether square (i, j) of the board is in it.
 */
export function caughtIn(inside, rule, objects, grid, extent) {
    const { cols, rows } = boardSquares(grid, extent);
    const has = (i, j) => i >= 0 && j >= 0 && i < cols && j < rows && inside(i, j);
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    const out = [];
    for (const o of objects) {
        if (o.kind !== 'token' || o.dead === true)
            continue;
        const across = onBoard(xs, o.x, o.size.w);
        const down = onBoard(ys, o.y, o.size.h);
        if (across === 'out' || down === 'out')
            continue;
        const box = footprintSquares(grid, extent, { x: o.x, y: o.y }, o.size);
        const space = [];
        for (let j = Math.max(0, box.j0); j <= Math.min(rows - 1, box.j1); j += 1)
            for (let i = Math.max(0, box.i0); i <= Math.min(cols - 1, box.i1); i += 1)
                space.push([i, j]);
        let caught;
        if (rule === 'any_part')
            caught = space.some(([i, j]) => has(i, j));
        else if (rule === 'all')
            caught = across === 'in' && down === 'in' && space.length > 0 && space.every(([i, j]) => has(i, j));
        else
            caught = o.x > xs[0] && o.x < xs[xs.length - 1] && o.y > ys[0] && o.y < ys[ys.length - 1]
                && centreIndices(ys, o.y).every((j) => centreIndices(xs, o.x).every((i) => has(i, j)));
        if (caught)
            out.push({ o, i: box.i0, j: box.j0 });
    }
    return out.sort((p, q) => p.j - q.j || p.i - q.i).map((x) => x.o);
}
//# sourceMappingURL=caught.js.map